// src/sw/policy.ts: what the service worker does with each request (docs/DECISIONS.md, "Installable app"). Pure, so
// every rule is unit tested (tests/app/sw-policy.test.ts); src/sw/sw.ts applies it.
//
// The worker is deliberately small. It caches only what cannot hold anyone's data and does not change under a URL:
// the build's fingerprinted files (/assets/), the builder's icons and logos (/brand/, the touch icon) and our own font
// files (Onest, /fonts/; no font is loaded from another host).
// A navigation always goes to the network (never a stale app shell after a deploy) and falls back to the offline page.
// Everything else is left to the browser, untouched.
//
// Never cached, whatever the route says:
//   - /api/*: board data, the session, photos (access-checked on every view: a cached copy would outlive sign-out)
//   - the sign-in pages and links: /signin, /auth/*, /unsubscribe, /start, /demo
//   - a URL that carries a token or a code (a query or fragment key such as t, token, code, key; a 6-digit value; a
//     long opaque value), or credentials
//   - a request with an Authorization header, and anything but GET
//   - a response that is not a plain 200, is opaque or redirected, says private or no-store, varies on everything,
//     or is HTML (an app shell, or the SPA fallback answering a missing file)
// There is no Set-Cookie rule: a worker never sees that header (fetch() filters it out of every response it hands
// over), so such a rule could never fire. What keeps cookies out is that only static routes are cacheable at all.

export type RouteKind = "pass" | "navigate" | "asset" | "icon" | "font";

/** The parts of a Request the rules read (a real Request fits). */
export type RequestLike = { url: string; method: string; mode?: string; headers?: { get(name: string): string | null } };
/** The parts of a Response the rules read (a real Response fits). */
export type ResponseLike = { status: number; type?: string; redirected?: boolean; headers: { get(name: string): string | null } };

/** Same-origin paths never cached: the API and the sign-in pages and links. */
export const NEVER_CACHE_PREFIXES = ["/api/", "/signin", "/auth/", "/unsubscribe", "/start", "/demo"] as const;
/** Query or fragment keys that carry a secret. */
export const SECRET_KEYS = [
  "t", "token", "code", "key", "k", "otp", "sig", "signature", "auth", "session", "access_token", "id_token",
  "refresh_token", "jwt", "password", "secret",
] as const;

const FONT_FILE = /\.(woff2?|ttf|otf)$/i;
const ICON_FILE = /\.(png|svg|webp|ico|jpe?g)$/i;
const SIX_DIGITS = /^\d{6}$/;
const OPAQUE_VALUE = /^[A-Za-z0-9_\-.~+/=]{24,}$/;

function parse(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

/** A query or fragment that carries a secret: a known key, a 6-digit value, or a long opaque value. */
export function carriesSecret(url: URL): boolean {
  if (url.username || url.password) return true;
  const params = [...url.searchParams.entries()];
  if (url.hash.length > 1) params.push(...new URLSearchParams(url.hash.slice(1)).entries());
  return params.some(([k, v]) => (SECRET_KEYS as readonly string[]).includes(k.toLowerCase()) || SIX_DIGITS.test(v) || OPAQUE_VALUE.test(v));
}

/** Why a request must never be cached, or null when the request itself is fine to cache. */
export function neverCacheRequest(req: RequestLike, origin: string): string | null {
  if (req.method.toUpperCase() !== "GET") return "method";
  const url = parse(req.url);
  if (!url || (url.protocol !== "https:" && url.protocol !== "http:")) return "scheme";
  if (url.origin === origin && NEVER_CACHE_PREFIXES.some((p) => url.pathname === p.replace(/\/$/, "") || url.pathname.startsWith(p))) {
    return url.pathname === "/api" || url.pathname.startsWith("/api/") ? "api" : "sign-in";
  }
  if (carriesSecret(url)) return "secret";
  if (req.headers?.get("authorization")) return "authorization";
  return null;
}

/** What the worker does with a request: pass it to the browser untouched, or one of its strategies. */
export function routeFor(req: RequestLike, origin: string): RouteKind {
  const url = parse(req.url);
  if (!url || req.method.toUpperCase() !== "GET") return "pass";
  const same = url.origin === origin;
  // A navigation is never cached, only answered from the network or with the offline page.
  if (req.mode === "navigate") return same ? "navigate" : "pass";
  if (neverCacheRequest(req, origin)) return "pass";
  if (same) {
    if (url.pathname.startsWith("/assets/")) return "asset";
    if (FONT_FILE.test(url.pathname)) return "font";
    if ((url.pathname.startsWith("/brand/") && ICON_FILE.test(url.pathname)) || /^\/apple-touch-icon(-precomposed)?\.png$/.test(url.pathname)) return "icon";
    return "pass";
  }
  return "pass";
}

/** Why a response must never be cached, or null when it may be. */
export function neverCacheResponse(res: ResponseLike): string | null {
  if (res.status !== 200) return "status";
  if (res.type && res.type !== "basic" && res.type !== "cors" && res.type !== "default") return "opaque";
  if (res.redirected) return "redirected";
  const cc = (res.headers.get("cache-control") ?? "").toLowerCase();
  if (/(^|[\s,])private\b/.test(cc)) return "private";
  if (/(^|[\s,])no-store\b/.test(cc)) return "no-store";
  if ((res.headers.get("vary") ?? "").trim() === "*") return "vary";
  if ((res.headers.get("content-type") ?? "").toLowerCase().includes("text/html")) return "html";
  return null;
}

/** The whole rule for storing a response: a cacheable route, a clean request and a clean response. */
export function mayCache(req: RequestLike, res: ResponseLike, origin: string): boolean {
  const kind = routeFor(req, origin);
  if (kind === "pass" || kind === "navigate") return false;
  return neverCacheRequest(req, origin) === null && neverCacheResponse(res) === null;
}

/** The page's kill switch tells a worker still controlling it to stop caching (it is unregistered right after). */
export const RETIRE_MESSAGE = "hb:retire";

/** This version's cache name. */
export function cacheName(version: string): string {
  return `hb-assets-${version}`;
}

/**
 * Caches to delete when `version` takes over: every hb-assets- cache except this version's and the newest older one,
 * which an open tab from before the deploy may still need for its lazy chunks. `keys` is in creation order
 * (CacheStorage). Other caches (a later phase's held photos, say) are not this rule's to delete.
 */
export function staleCaches(keys: readonly string[], version: string): string[] {
  const current = cacheName(version);
  const older = keys.filter((k) => k.startsWith("hb-assets-") && k !== current);
  const keep = older[older.length - 1];
  return older.filter((k) => k !== keep);
}
